<template>
  <GlobalSidebarContainer>
    <div v-if="languageLoaded" class="sidebar-inner-container">
      <SubscriptionInfoButton
        v-if="showSubscriptionButton"
        :days-left="daysLeft"
        :compact="compact"
        @click="$emit('open-subscription-modal')"
      />
      <router-link
        v-for="item in sidebarItemsStore.sidebarItems"
        :key="item.pathName"
        v-slot="{ href, isActive, navigate }"
        :to="{ name: item.pathName }"
        custom
      >
        <a
          v-wave
          v-tooltip="{
            content: compact && $t(item.label),
            distance: 12,
            placement: Placement.Right,
            delay: { show: 300, hide: 100 },
          }"
          :class="{ 'is-active': isPathActive(item.pathName, isActive) }"
          class="sidebar-button"
          :href="href"
          :data-external="`global-sidebar-:${item.label}-button`"
          @click="navigate"
        >
          <GlobalSidebarIconActive
            v-if="isPathActive(item.pathName, isActive)"
            class="icon-wrapper"
            :type="item.label"
          />
          <GlobalSidebarIcon v-else class="icon-wrapper" :type="item.label" />
          <template v-if="!compact">
            <FriedH4 v-if="isPathActive(item.pathName, isActive)" class="sidebar-label">{{
              $t(item.label)
            }}</FriedH4>
            <FriedParagraph v-else class="sidebar-label">{{ $t(item.label) }}</FriedParagraph>
            <FriedStatusBadge
              v-if="item.beta"
              class="beta-badge"
              text="Beta"
              :status-type="StatusType.Branded"
              :enable-min-width="false"
            />
          </template>
          <FriedStatusBadge
            v-if="compact && item.beta"
            class="beta-dot"
            text="Beta"
            :status-type="StatusType.Branded"
            :enable-min-width="false"
          />
        </a>
      </router-link>

      <ProductSwitcher
        v-if="showProductSwitcher"
        class="module-switcher"
        @set-module-type="$emit('set-module-type', $event)"
      />
    </div>
  </GlobalSidebarContainer>
</template>

<script lang="ts">
import { onMounted, defineComponent, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { FriedStatusBadge, StatusType, Placement } from '@getaccept/fried-tofu';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useRoute } from 'vue-router';
import { languageLoaded } from '../../lang';
import { useSidebarItemsStore } from '../store/sidebar-items.store';
import { useModulesStore } from '../../modules/store/modules.store';
import SubscriptionInfoButton from '../../subscription/components/SubscriptionInfoButton.vue';
import { useSubscriptionStore } from '../../subscription/store/subscription.store';
import GlobalSidebarIconActive from './GlobalSidebarIconActive.vue';
import GlobalSidebarIcon from './GlobalSidebarIcon.vue';
import ProductSwitcher from './ProductSwitcher.vue';
import { useSidebarStore } from './store/sidebar.store';
import GlobalSidebarContainer from './GlobalSidebarContainer.vue';

export default defineComponent({
  components: {
    GlobalSidebarContainer,
    GlobalSidebarIconActive,
    GlobalSidebarIcon,
    ProductSwitcher,
    FriedStatusBadge,
    SubscriptionInfoButton,
  },
  emits: ['open-subscription-modal', 'set-module-type'],
  setup() {
    const { isXSmallScreen, isXXSmallScreen } = useBreakpoints();
    const store = useSidebarStore();
    const { compact } = storeToRefs(store);
    const sidebarItemsStore = useSidebarItemsStore();
    const modulesStore = useModulesStore();

    const subscriptionStore = useSubscriptionStore();

    const { isInactiveSubscription, isTrialPlan, daysLeft } = storeToRefs(subscriptionStore);

    const showSubscriptionButton = computed(
      () => isTrialPlan.value && !isInactiveSubscription.value
    );

    const showProductSwitcher = computed(
      () =>
        modulesStore.hasContractRoomModuleAccess &&
        modulesStore.hasDealRoomModuleAccess &&
        !isInactiveSubscription.value
    );

    const setDefaultMenuMode = () => {
      if (isXSmallScreen.value || isXXSmallScreen.value) {
        store.setCompact(true);
      }
    };

    onMounted(() => {
      setDefaultMenuMode();
      window.addEventListener('resize', setDefaultMenuMode);
    });

    const route = useRoute();

    const isPathActive = (path, isActive) => {
      if (isActive) return true;
      return route.path.includes(path);
    };

    return {
      compact,
      toggleSidebar: store.toggleSideBar,
      isPathActive,
      languageLoaded,
      StatusType,
      Placement,
      showSubscriptionButton,
      sidebarItemsStore,
      modulesStore,
      isInactiveSubscription,
      daysLeft,
      showProductSwitcher,
    };
  },
});
</script>

<style lang="scss" scoped>
.sidebar-inner-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  @media print {
    display: none;
  }

  .sidebar-button {
    position: relative;
    width: 100%;
    background: transparent;
    text-decoration: none;
    border: 0;
    display: flex;
    align-items: center;
    padding: 0 var(--spacing-50);
    border-radius: 0.375rem;
    height: 2.5rem;
    color: var(--text-dark);
    min-width: 3.375rem;
    margin-bottom: var(--spacing-50);

    .icon-wrapper {
      width: 1.5rem;
      height: 1.5rem;
      margin-left: var(--spacing-50);
    }

    .beta-badge {
      flex-shrink: 0;
    }

    .beta-dot {
      position: absolute;
      top: var(--spacing-75);
      right: var(--spacing-50);
      max-width: 10px;
      height: 10px;
      overflow: hidden;
      transition: all 0.2s;
      padding: 0;
      border-radius: 10px;
      background: var(--brand-orange);
      transform: translate(100%, -100%);

      :deep(.text) {
        color: transparent;
      }
    }

    .sidebar-label {
      cursor: pointer;
      /* stylelint-disable-next-line value-no-vendor-prefix */
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;
      width: inherit;
      margin-left: var(--spacing-100);
    }

    &:hover {
      background: var(--gray-96);
    }

    &.is-active {
      background: var(--gray-94);
    }

    &:focus[data-focus-visible-added] {
      background: var(--gray-94);
      outline: -webkit-focus-ring-color auto 1px;
    }

    &:hover,
    &:focus[data-focus-visible-added] {
      .beta-dot {
        max-width: 5rem;
        height: 1.5rem;
        padding: var(--spacing-25) var(--spacing-50);
        border-radius: var(--radius-small);

        :deep(.text) {
          color: var(--white);
        }
      }
    }
  }
}

.module-switcher {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
